// Pairing and token requests through the shell (Electron, review CR8B-1): the main process
// makes `POST /devices/register` and `/devices/token` for the page, sealing the refresh secret
// on the way in and unsealing it on the way out, so the page only ever holds the sealed form
// (shells/electron/src/device-secret.ts). The page's ApiClient is given a `fetch` that sends
// just those two requests to the shell; everything else goes out as before.

/** What the shell answers: the API's status and body, or "no answer" (network). */
export type DeviceAuthHostResult = { status: number; body: string } | { network: string };

/** `window.stationHost.deviceAuth` in the Electron preload. */
export interface DeviceAuthHostApi {
  request(path: string, baseUrl: string, body: string): Promise<DeviceAuthHostResult>;
}

const PATHS = ['/devices/register', '/devices/token'];

/**
 * A `fetch` for an ApiClient at `baseUrl` that sends pairing and token requests through
 * `host` and everything else through `inner`.
 */
export function deviceAuthFetch(
  host: DeviceAuthHostApi,
  baseUrl: string,
  inner: typeof fetch = (input, init) => globalThis.fetch(input, init),
): typeof fetch {
  const base = baseUrl.replace(/\/+$/, '');
  return async (input, init) => {
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
    const path = url.startsWith(base) ? url.slice(base.length) : undefined;
    if (path === undefined || !PATHS.includes(path) || (init?.method ?? 'GET') !== 'POST') {
      return inner(input, init);
    }
    const result = await host.request(path, base, typeof init?.body === 'string' ? init.body : '');
    if ('network' in result) throw new TypeError(result.network);
    return new Response(result.body, {
      status: result.status,
      headers: { 'content-type': 'application/json' },
    });
  };
}
